/**
 * useAlertNotifications — Hook for managing real-time alert notifications via WebSocket.
 *
 * Listens for `alert:triggered` and `alert:resolved` events from the Socket context
 * and maintains a queue of notification items to display as toasts/snackbars.
 *
 * Features:
 * - Queues incoming alert events for sequential display
 * - Auto-dismisses notifications after a configurable duration
 * - Supports manual dismissal
 * - Limits the maximum number of visible notifications
 * - Invalidates alert history queries so the list stays fresh
 *
 * Validates: Task 25.5 — Build real-time alert notifications via WebSocket
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AlertTriggeredPayload, AlertResolvedPayload } from 'shared';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface AlertNotification {
  /** Unique ID for this notification instance */
  id: string;
  /** Type of alert event */
  type: 'triggered' | 'resolved';
  /** Alert ID from the backend */
  alertId: string;
  /** Server ID that triggered the alert */
  serverId: string;
  /** Metric name (e.g., 'cpu', 'memory') */
  metricName: string;
  /** Current metric value that breached/recovered */
  metricValue?: number;
  /** Threshold value configured in the rule */
  thresholdValue?: number;
  /** Alert severity */
  severity?: 'warning' | 'critical';
  /** Timestamp of the event */
  timestamp: string;
  /** Whether this notification is currently visible */
  open: boolean;
}

export interface UseAlertNotificationsOptions {
  /** Maximum number of notifications to show simultaneously (default: 3) */
  maxVisible?: number;
  /** Auto-dismiss duration in milliseconds (default: 8000 for triggered, 5000 for resolved) */
  triggeredDuration?: number;
  resolvedDuration?: number;
}

export interface UseAlertNotificationsResult {
  /** Currently active notifications to render */
  notifications: AlertNotification[];
  /** Dismiss a specific notification by ID */
  dismiss: (id: string) => void;
  /** Dismiss all notifications */
  dismissAll: () => void;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const DEFAULT_MAX_VISIBLE = 3;
const DEFAULT_TRIGGERED_DURATION = 8000;
const DEFAULT_RESOLVED_DURATION = 5000;

let notificationCounter = 0;

function generateNotificationId(): string {
  notificationCounter += 1;
  return `alert-notif-${Date.now()}-${notificationCounter}`;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useAlertNotifications(
  options: UseAlertNotificationsOptions = {}
): UseAlertNotificationsResult {
  const {
    maxVisible = DEFAULT_MAX_VISIBLE,
    triggeredDuration = DEFAULT_TRIGGERED_DURATION,
    resolvedDuration = DEFAULT_RESOLVED_DURATION,
  } = options;

  const { onAlertTriggered, onAlertResolved } = useSocket();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const companyId = user?.companyId;

  const [notifications, setNotifications] = useState<AlertNotification[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // ─── Auto-dismiss timer management ─────────────────────────────────────

  const scheduleAutoDismiss = useCallback(
    (id: string, duration: number) => {
      const timer = setTimeout(() => {
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, open: false } : n))
        );
        timersRef.current.delete(id);

        // Remove from list after exit animation completes
        setTimeout(() => {
          setNotifications((prev) => prev.filter((n) => n.id !== id));
        }, 300);
      }, duration);

      timersRef.current.set(id, timer);
    },
    []
  );

  // ─── Dismiss handlers ──────────────────────────────────────────────────

  const dismiss = useCallback((id: string) => {
    // Clear auto-dismiss timer if exists
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }

    // Start close animation
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, open: false } : n))
    );

    // Remove after animation
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 300);
  }, []);

  const dismissAll = useCallback(() => {
    // Clear all timers
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current.clear();

    // Close all
    setNotifications((prev) => prev.map((n) => ({ ...n, open: false })));

    // Remove all after animation
    setTimeout(() => {
      setNotifications([]);
    }, 300);
  }, []);

  // ─── WebSocket event listeners ─────────────────────────────────────────

  useEffect(() => {
    if (!companyId) return;

    const unsubTriggered = onAlertTriggered((payload: AlertTriggeredPayload) => {
      const id = generateNotificationId();
      const notification: AlertNotification = {
        id,
        type: 'triggered',
        alertId: payload.alertId,
        serverId: payload.serverId,
        metricName: payload.metricName,
        metricValue: payload.metricValue,
        thresholdValue: payload.thresholdValue,
        severity: payload.severity,
        timestamp: payload.triggeredAt,
        open: true,
      };

      setNotifications((prev) => {
        // Enforce max visible — remove oldest if at limit
        const updated = [...prev, notification];
        if (updated.length > maxVisible) {
          const removed = updated.shift();
          if (removed) {
            const timer = timersRef.current.get(removed.id);
            if (timer) {
              clearTimeout(timer);
              timersRef.current.delete(removed.id);
            }
          }
        }
        return updated;
      });

      scheduleAutoDismiss(id, triggeredDuration);

      // Invalidate alert history so the list refreshes
      queryClient.invalidateQueries({ queryKey: ['alertHistory', companyId] });
    });

    const unsubResolved = onAlertResolved((payload: AlertResolvedPayload) => {
      const id = generateNotificationId();
      const notification: AlertNotification = {
        id,
        type: 'resolved',
        alertId: payload.alertId,
        serverId: payload.serverId,
        metricName: '',
        timestamp: payload.resolvedAt,
        open: true,
      };

      setNotifications((prev) => {
        const updated = [...prev, notification];
        if (updated.length > maxVisible) {
          const removed = updated.shift();
          if (removed) {
            const timer = timersRef.current.get(removed.id);
            if (timer) {
              clearTimeout(timer);
              timersRef.current.delete(removed.id);
            }
          }
        }
        return updated;
      });

      scheduleAutoDismiss(id, resolvedDuration);

      // Invalidate alert history so the list refreshes
      queryClient.invalidateQueries({ queryKey: ['alertHistory', companyId] });
    });

    return () => {
      unsubTriggered();
      unsubResolved();
    };
  }, [
    companyId,
    maxVisible,
    triggeredDuration,
    resolvedDuration,
    onAlertTriggered,
    onAlertResolved,
    scheduleAutoDismiss,
    queryClient,
  ]);

  // ─── Cleanup timers on unmount ─────────────────────────────────────────

  useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current.clear();
    };
  }, []);

  return { notifications, dismiss, dismissAll };
}
